<template lang="pug">
  .login-with-password
    .top-wrap
      p.sign-in-title.kapra-neue.upper.xs-f-xgrande.max-at-tweak Welcome Back
      p.text-color-2.xs-f-small.max-at-tweak
        | Your email address&#32;
        br
        span.user-email.semi-bold {{ userEmail }}
        | &#32;
        button.change-email.btn-reset.text-color-3.underline(type="button" @click="setView('signIn')") Change

    .middle-wrap
      p.text-color-2.xs-f-small.max-at-tweak You can request a verification link to sign in without your password, or enter your password.

      .radio-wrap
        mrRadio#login_type_password(type="radio" v-model="loginType" value="password" name="login_type")
          label(for="login_type_password") Enter my password

      transition-expand
        form#login_with_password_form(v-if="loginType == 'password'" name="login_with_password_form" @submit.prevent="signIn" novalidate)
          .password-input.mr-input-wrap
            MrPasswordInput(v-model="password" autocomplete="current-password" data-mr-ass="sign-in-password" name="password")
            button.forgot-password.btn-reset.underline.text-color-3.xs-f-small.max-at-tweak(type="button" @click="forgotPassword" data-mr-ass="forgot-password") Forgot Password

          .mr-input-wrap
            mrInputCheckbox.xs-mt-100m.xs-mb-100m(v-model="staySignedIn" :data-mr-ass="'keep-me-signed-in'" :label="'Keep me signed in'")

          .passkey-toggle-wrap.mb-100m(v-if="showPasskeysToggle")
            mr-icon.face-icon(name="face-id" height="1.5em")
            span.xs-12.face-text.text-left.xs-f-small.max-at-tweak {{ passkeyText }}
            ToggleInput(v-model="usePasskey")

          .captcha(v-if="showCaptcha")
            Captcha(@captcha-response="setCaptchaResponse")

          button.mr-btn.btn-block.font-xsmall(type="submit" :disabled="v$.$invalid || processing" :class="{ disabled: v$.$invalid || processing }" data-mr-ass="sign-in") Continue

      .radio-wrap
        mrRadio#login_type_magic_link(type="radio" v-model="loginType" value="oneTimeLink" name="login_type")
          label(for="login_type_magic_link") Send me a one-time link
        p.xs-f-xsmall.max-at-tweak You can request a verification link to sign in without your password.

      transition(name="fade")
        .magic-cta-wrap
          button.mr-btn.btn-block.xs-f-xsmall.max-at-tweak(v-if="loginType == 'oneTimeLink'" type="button" @click="magicLink" data-mr-ass="magic-link") Request a one-time link

</template>

<script>
  import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
  import { required } from "@vuelidate/validators";
  import { sendMagicLink } from "@services/vueCustomerSvc";

  import Captcha from "@components/captcha";
  import MrPasswordInput from '@components/MrPasswordInput';
  import mrInputCheckbox from "@components/mrInputCheckbox";
  import mrRadio from '@components/mrRadio';
  import ToggleInput from '@components/ToggleInput';
  import MrIcon from '@components/MrIcon';
  import { useVuelidate } from "@vuelidate/core";

  export default {
    name: "LoginWithPassword",

    components: {
      Captcha,
      MrPasswordInput,
      mrInputCheckbox,
      mrRadio,
      ToggleInput,
      MrIcon,
    },

    setup() {
      return {
        v$: useVuelidate()
      };
    },

    data() {
      return {
        loginType: 'password',
        forgotError: null,
        magicLinkSent: null,
      };
    },

    computed: {
      ...mapGetters('global', ['urlParams']),

      ...mapState('signIn', [
        'userEmail',
        'processing',
        'showCaptcha',
        'userPassword',
        'usePasskeys',
        'keepMeSignedIn',
        'fidoData',
      ]),

      ...mapState('systemSettings', ['passkeysSettings']),
      ...mapState('global', ['windowData']),

      breakpoint() {
        return this.windowData.breakpoint;
      },

      password: {
        get() {
          return this.userPassword;
        },

        set(value) {
          this.setUserPassword(value);
        }
      },

      showPasskeysToggle() {
        return this.breakpoint == 'mobile' && this.passkeysSettings?.enabled;
      },

      usePasskey: {
        get() {
          return this.usePasskeys;
        },

        set(value) {
          this.trackMREvent(`MrSignInV2 - Passkeys toggle changed ${value ? 'ON' : 'OFF'}`);
          this.setUsePasskeys(value);
        }
      },

      passkeyText() {
        return "Use passkeys";
      },

      staySignedIn: {
        get() {
          return this.keepMeSignedIn;
        },

        set(val) {
          this.setKeepMeSignedIn(val);
        }
      },
    },

    validations: {
      password: {
        required,
      }
    },

    methods: {
      ...mapActions([
        'notifyError',
      ]),

      ...mapActions('signIn', [
        'signIn',
        'afterSignin'
      ]),

      ...mapMutations('signIn', [
        'setUserPassword',
        'setCaptchaResponse',
        'setKeepMeSignedIn',
        'setUsePasskeys',
        'setView',
        'setProcessing',
        'setOnSuccessCallback',
      ]),

      forgotPassword() {
        this.trackMREvent('MrSignInV2 - Forgot password clicked', { email: this.userEmail });
        this.setView('forgotPassword');
      },

      magicLink() {
        if (this.processing) {
          return;
        }

        this.setProcessing(true);

        sendMagicLink({ email: this.userEmail }).then(() => {
          this.setView('magicLinkSent');
          this.trackMREvent('MrSignInV2 - Magic link email sent', { email: this.userEmail });
          this.setProcessing(false);
        }).catch(err => {
          this.setProcessing(false);
          this.trackMREvent('MrSignInV2 - Magic link email send failed', { email: this.userEmail });

          let error = err.response;
          let errorMessage = "";

          if (error.data) {
            if (error.data.message == "Invalid e-mail address") {

              setTimeout(() => {
                window.location = "/support";
              }, 4000);

              errorMessage = error.data.message + ", redirecting to support page, one moment.";
            } else if (error.data.code == "UNSUBSCRIBED") {
              errorMessage = "It looks like you might not be getting our emails. If you do not receive one shortly please contact Color Crew for assistance.";
            } else {
              errorMessage = error.data.message;
            }

            this.notifyError(errorMessage);
          }
        });
      },

      showAppDownloadModal() {
        this.setOnSuccessCallback(null);
        this.$cookies.set('afterLoginModalViewed', 1, '30d');
        this.trackMREvent('App Download Modal Viewed');

        this.showModal({
          component: './MrSignIn/AfterSignInModal',
          onClose: () => {
            this.trackMREvent('App Download Modal Closed');
            this.afterSignin();
          },
          theme: 'signin'
        });
      },
    },

    mounted() {
      if (!(this.urlParams && this.urlParams.next)) {
        this.mix_trackExperimentViewed('Download App After Login 2023');
        if (this.experiments['Download App After Login 2023'] && this.experiments['Download App After Login 2023'] == 'B' && !this.$cookies.get('afterLoginModalViewed')) {
          this.setOnSuccessCallback(this.showAppDownloadModal);
        }
      }

      if (!this.showPasskeysToggle) {
        this.setUsePasskeys(false);
      }
    }
  };
</script>

<style scoped lang="stylus">
  .login-with-password
    .middle-wrap
      padding-top 2em

    #login_with_password_form
      padding-bottom 1.5em

      .password-input
        position relative

        .forgot-password
          position absolute
          right 0
          bottom 0

      .passkey-toggle-wrap
        display flex
        align-items center
        justify-content space-between
        margin-bottom 1.5em
        :deep(.toggle-input)
          min-width 2em
          &.focused
            outline cta-color-1 auto 5px
          &.checked
            background-color cta-color-1
            border-color cta-color-1
        .face-icon
          height 21px
          min-height 21px
          width 21px
          min-width 21px
          margin-right 0.5em
        .face-text
          font-size 14px


    .magic-cta-wrap
      padding-top 1.5em

    .radio-wrap
      background color-white

      :deep(.radio-label)
        display block
        padding 0.5em 0

    .fade-enter-active,
    .fade-leave-active
      transition opacity .5s

    .fade-enter,
    .fade-leave-to
      opacity 0

</style>